<template>
  <div>
    <navbar :links="links" />
    <v-layout
      wrap
      align-center
      class="mt-3 mb-3"
    >
      <!-- Search -->
      <v-text-field
        v-model="campaignsSearchText"
        class="searchbar-input ml-0 mr-auto mb-3"
        hide-details
        :label="searchLabel"
        clearable
        append-icon="search"
        @click:clear="() => (campaignsSearchText = '')"
        @keyup.native.esc="campaignsSearchText = ''"
      />
      <!-- Create new button -->
      <v-btn
        v-if="showCreateCampaignButton"
        round
        dark
        color="msr"
        class="ml-0"
        :to="{name: 'campaigncreate'}"
      >
        <v-icon left>
          add
        </v-icon>
        <span>{{ $t('campaignCreateNew') }}</span>
      </v-btn>
    </v-layout>
    <!-- Loading Indicator -->
    <div class="loading-indicator">
      <v-progress-circular
        v-if="loading && campaigns.length === 0"
        :size="50"
        indeterminate
        class="mt-5 msr--text"
      />
    </div>
    <!-- 'No entries' -->
    <v-list v-if="!loading && campaigns.length === 0">
      <v-list-tile>
        <v-list-tile-sub-title>{{ $t('noCampaigns') }}</v-list-tile-sub-title>
      </v-list-tile>
    </v-list>
    <!-- Upper Pagination Controls -->
    <pagination-controls
      :show="campaignsNumPages > 1"
      :page.sync="campaignsPageIndex"
      :num-pages="campaignsNumPages"
    />
    <!-- Campaigns List -->
    <v-list
      v-if="campaigns.length"
      avatar
      dense
      three-line
    >
      <template v-for="(campaign, campaignIndex) in campaigns">
        <v-card
          :key="campaign.id"
          flat
          data-cy="campaign-card"
          :style="{opacity: loading ? 0.5 : 1.0}"
        >
          <span :key="campaignIndex">
            <v-list-tile
              class="campaign-tile mb-1 no-hover-clr"
              dense
              @click="goToCampaign(campaign)"
            >
              <!-- Unpublished Icon -->
              <v-avatar
                v-if="!campaign.published"
                :title="$t('campaignNotPublished')"
              >
                <v-icon color="msr">visibility_off</v-icon>
              </v-avatar>
              <!-- Activation Status Icon -->
              <span v-else>
                <v-avatar v-if="campaignIsFullyActivated(campaign)">
                  <v-icon
                    large
                    class="success--text"
                  >check_circle</v-icon>
                </v-avatar>
                <v-avatar v-else>
                  <v-icon
                    large
                    class="msr--text"
                  >flag</v-icon>
                  <v-icon
                    v-if="campaign.activation_date"
                    class="success--text"
                    style="position: absolute; left: 10px; top: 12px"
                  >check_circle</v-icon>
                </v-avatar>
              </span>
              <v-list-tile-content>
                <v-list-tile-title>
                  <span class="grey--text text--darken-3">
                    <strong style="font-size: 1.25em">{{
                      campaign.title
                    }}</strong>
                  </span>
                </v-list-tile-title>
                <v-list-tile-sub-title>
                  <v-layout column>
                    <div
                      v-if="!campaign.published"
                      class="msr--text"
                    >
                      {{ $t('campaignNotPublished') }}
                    </div>
                    <div
                      v-if="campaign.expiry_date && !campaign.activation_date"
                      class="warning--text"
                    >
                      {{ $t('expiresOn') }}
                      {{
                        subtractOneDayFromDate(campaign.expiry_date)
                          | dayjs('DD.MM.YYYY')
                      }}
                    </div>
                    <div
                      v-if="campaign.activation_date"
                      class="success--text"
                    >
                      <span v-if="campaignIsFullyActivated(campaign)">{{
                        $t('activatedOn')
                      }}</span>
                      <span v-else>{{ $t('partiallyActivatedOn') }}</span>
                      {{ campaign.activation_date | dayjs('DD.MM.YYYY') }}
                      <span
                        v-if="campaignIsFullyActivated(campaign)"
                      >({{ $t('all') }}</span>
                      <span
                        v-else
                      >({{ campaign.activations.length }}/{{
                        availableStoresForCampaign(campaign).length
                      }}</span>
                      {{ $t('stores') }})
                    </div>
                  </v-layout>
                </v-list-tile-sub-title>
              </v-list-tile-content>
              <v-list-tile-avatar>
                <v-icon
                  medium
                  class="grey--text"
                >chevron_right</v-icon>
              </v-list-tile-avatar>
            </v-list-tile>
            <v-card
              flat
              class="mt-2"
            >
              <!-- Placeholder while images are loaded. -->
              <v-progress-circular
                v-if="shouldShowPlaceHolderForCampaign(campaign)"
                :size="40"
                :width="10"
                indeterminate
                class="grey--text text--lighten-4 image-placeholder"
              />
              <v-img
                v-if="campaign.poster"
                class="clickable"
                :height="$vuetify.breakpoint.xsOnly ? 95 : 240"
                style="max-width: 800px"
                contain
                :src="campaign.poster"
                @click="goToCampaign(campaign)"
              />
              <!-- This element only exists to handle the load event. -->
              <img
                :src="campaign.poster"
                width="0"
                height="0"
                @load="loadedImage(campaign.poster)"
              >
            </v-card>
          </span>
        </v-card>
        <v-divider
          v-if="campaignIndex !== campaigns.length - 1"
          :key="'divider' + campaign.id"
          class="my-3"
        />
      </template>
    </v-list>
    <!-- Lower Pagination Controls -->
    <pagination-controls
      :show="campaignsNumPages > 1 && campaigns.length > 10"
      :page.sync="campaignsPageIndex"
      :num-pages="campaignsNumPages"
    />
  </div>
</template>

<script>
import debounce from 'lodash.debounce';

import campaigns from '@/mixins/campaigns';
import timing from '@/mixins/timing';

import Navbar from '@/components/Navbar';
import PaginationControls from '@/components/PaginationControls';

export default {
  components: {
    Navbar,
    PaginationControls,
  },
  mixins: [campaigns, timing],
  data() {
    return {
      links: ['dashboard', 'campaigns'],
      loading: false,
      imageToLoaded: {},
      preventFetchOnNextPageIndexWatcherTick: false,
    };
  },
  computed: {
    campaignsPageIndex: {
      get() {
        return this.$store.state.common.campaignsPageIndex;
      },
      set(index) {
        this.$store.commit('SET_CAMPAIGNS_PAGE_INDEX', index);
      },
    },
    campaignsSearchText: {
      get() {
        return this.$store.state.common.campaignsSearchText;
      },
      set(text) {
        if (this.campaignsPageIndex > 1) {
          // We don't want to trigger a request by resetting the index,
          // since we'll send one due to changing the searchtext anyway.
          this.preventFetchOnNextPageIndexWatcherTick = true;
          this.$store.commit('RESET_CAMPAIGNS_PAGE_INDEX');
        }
        this.$store.commit('SET_CAMPAIGNS_SEARCH_TEXT', text);
      },
    },
    campaigns() {
      return this.$store.state.common.campaignsPage.results;
    },
    campaignsNumPages() {
      return this.$store.state.common.campaignsPage.num_pages;
    },
    campaignsOverallCount() {
      return this.$store.state.common.campaignsPage.count;
    },
    searchLabel() {
      return this.$t('search') + ` (${this.campaignsOverallCount || 0}) ...`;
    },
    showCreateCampaignButton() {
      return (
        !this.loading &&
        this.$store.getters.userCanUploadContent &&
        !!this.$user &&
        !!this.$userprofile &&
        !!this.$userprofile.workspace &&
        !!this.$userprofile.workspace.campaign_creation_enabled
      );
    },
  },
  watch: {
    campaignsPageIndex: function () {
      if (this.preventFetchOnNextPageIndexWatcherTick) {
        this.preventFetchOnNextPageIndexWatcherTick = false;
      } else {
        this.refetchCampaigns();
      }
    },
    campaignsSearchText: debounce(function () {
      this.refetchCampaigns();
    }, 250),
  },
  mounted() {
    // Fetch initial campaigns list e.g. when this page is loaded directly
    // via URL. Subsequent loads will be triggered by the drawer action.
    if (this.campaigns.length === 0) {
      this.loading = true;
    }
    this.$store
      .dispatch('MAYBE_LIST_CAMPAIGNS')
      .then(() => (this.loading = false));

    // Available stores are used to mark Campaigns as fully/partially
    // activated.
    this.$store.dispatch('LIST_USER_WRITABLE_STORES');
  },
  methods: {
    refetchCampaigns() {
      this.loading = true;
      this.$store.dispatch('LIST_CAMPAIGNS').then(() => (this.loading = false));
    },
    shouldShowPlaceHolderForCampaign(campaign) {
      return campaign.poster && !this.imageToLoaded[campaign.poster];
    },
    /**
     * Mark image as having finished loading.
     *
     * This means we can hide its placeholder as it is no longer needed.
     */
    loadedImage(image) {
      this.$set(this.imageToLoaded, image, true);
    },
    campaignIsFullyActivated(campaign) {
      const stores = this.availableStoresForCampaign(campaign);
      return (
        campaign.activations.length > 0 &&
        campaign.activations.length === stores.length
      );
    },
    goToCampaign(campaign) {
      const path = '/campaigns/' + campaign.id;
      this.$router.push(path);
    },
  },
};
</script>

<style scoped>
.campaign-tile:hover .v-list__tile__title {
  text-decoration: underline;
}
.campaign-tile:hover .v-list__tile__avatar .v-avatar i {
  font-size: 40px !important;
}

.tools {
  display: flex;
  align-items: center;
}

.searchbar-input {
  max-width: 400px;
}

.image-placeholder {
  position: absolute;
  top: calc(50% - 20px);
  left: calc(50% - 20px);
}
</style>



// WEBPACK FOOTER //
// src/pages/Campaigns.vue